import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../Firebase";
import "../../Admin.css";
import AdminNav from "../../components/AdminNav";

function safeStr(v) {
  return String(v || "").trim();
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtDate(v) {
  if (!v) return "";
  if (typeof v?.toDate === "function") return v.toDate().toLocaleDateString();
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleDateString();
    return v;
  }
  return "";
}

function statusClass(status) {
  const s = safeStr(status).toLowerCase();
  if (s === "paid") return "pill ok";
  if (s === "partial") return "pill info";
  if (s === "overdue") return "pill warn";
  if (s === "void") return "pill bad";
  return "pill";
}

export default function AdminInvoices() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [rows, setRows] = useState([]);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("updated"); // updated | created | issue | due

  useEffect(() => {
    document.title = "Invoices | Admin";
  }, []);

  async function load() {
    setLoading(true);
    setErr("");

    try {
      // Default query: newest updated
      const q = query(collection(db, "publicInvoices"), orderBy("updatedAt", "desc"));
      const snap = await getDocs(q);

      const list = snap.docs.map((d) => {
        const x = d.data() || {};
        const customerName =
          x.customer?.companyName ||
          x.customer?.name ||
          "";
        const siteName =
          x.site?.propertyName ||
          x.site?.siteAddress?.line1 ||
          "";

        const invoiceNumber = x.invoiceNumber || d.id;

        const issueDate = x.issueDate || x.issuedAt || x.createdAt;
        const dueDate = x.dueDate || "";

        const total = x.totals?.total ?? x.total ?? 0;
        const balanceDue = x.totals?.balanceDue ?? x.balanceDue ?? total;

        return {
          id: d.id,
          invoiceNumber,
          status: x.status || "open",
          customerName,
          siteName,
          issueDate,
          dueDate,
          total: safeNum(total),
          balanceDue: safeNum(balanceDue),
          createdAt: x.createdAt || null,
          updatedAt: x.updatedAt || null,
          _raw: x,
        };
      });

      setRows(list);
    } catch (e) {
      setErr(e?.message || "Failed to load invoices.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sorted = useMemo(() => {
    const copy = [...rows];

    const pick = (r) => {
      if (sort === "created") return r.createdAt;
      if (sort === "issue") return r.issueDate;
      if (sort === "due") return r.dueDate;
      return r.updatedAt;
    };

    copy.sort((a, b) => {
      const av = pick(a);
      const bv = pick(b);

      const at = typeof av?.toDate === "function" ? av.toDate().getTime() : new Date(av || 0).getTime();
      const bt = typeof bv?.toDate === "function" ? bv.toDate().getTime() : new Date(bv || 0).getTime();
      return bt - at;
    });

    return copy;
  }, [rows, sort]);

  const filtered = useMemo(() => {
    const q = safeStr(search).toLowerCase();

    return sorted.filter((r) => {
      if (status !== "all" && safeStr(r.status).toLowerCase() !== status) return false;

      if (!q) return true;

      return (
        safeStr(r.invoiceNumber).toLowerCase().includes(q) ||
        safeStr(r.customerName).toLowerCase().includes(q) ||
        safeStr(r.siteName).toLowerCase().includes(q) ||
        safeStr(r.id).toLowerCase().includes(q)
      );
    });
  }, [sorted, search, status]);

  return (
    <div className="aiPage">
      <AdminNav />
      <div className="aiTop">
        <div>
          <h1>Invoices</h1>
          <p className="aiSub">Public invoice documents with print-friendly views.</p>
        </div>

        <div className="aiActions">
          <button className="btn btnGhost" onClick={load} disabled={loading}>
            Refresh
          </button>
          <button
            className="btn btnPrimary"
            onClick={() => nav("/admin/invoices/new")}
          >
            + New Invoice
          </button>
        </div>
      </div>

      <div className="aiControls">
        <input
          className="aiSearch"
          placeholder="Search invoice #, customer, site, blob ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select className="aiSelect" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="open">open</option>
          <option value="partial">partial</option>
          <option value="paid">paid</option>
          <option value="overdue">overdue</option>
          <option value="void">void</option>
        </select>

        <select className="aiSelect" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="updated">Sort: Updated</option>
          <option value="created">Sort: Created</option>
          <option value="issue">Sort: Issue date</option>
          <option value="due">Sort: Due date</option>
        </select>
      </div>

      {err ? <div className="aiError">{err}</div> : null}

      <div className="aiTableWrap">
        <table className="aiTable">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Status</th>
              <th>Customer</th>
              <th>Site</th>
              <th>Issue</th>
              <th>Due</th>
              <th className="num">Total</th>
              <th className="num">Balance</th>
              <th className="actions">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="aiLoading">
                  Loading…
                </td>
              </tr>
            ) : filtered.length ? (
              filtered.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="aiStrong">{r.invoiceNumber}</div>
                    <div className="aiMono">{r.id}</div>
                  </td>
                  <td>
                    <span className={statusClass(r.status)}>{r.status}</span>
                  </td>
                  <td>{r.customerName || <span className="aiMuted">—</span>}</td>
                  <td>{r.siteName || <span className="aiMuted">—</span>}</td>
                  <td>{fmtDate(r.issueDate) || <span className="aiMuted">—</span>}</td>
                  <td>{fmtDate(r.dueDate) || <span className="aiMuted">—</span>}</td>
                  <td className="num">${r.total.toLocaleString()}</td>
                  <td className="num">${r.balanceDue.toLocaleString()}</td>
                  <td className="actions">
                    <div className="aiRowActions">
                      <Link className="btn btnTiny" to={`/admin/invoices/edit/${r.id}`}>
                        Edit
                      </Link>
                      <a className="btn btnTiny btnGhost" href={`/invoice/${r.id}`} target="_blank" rel="noreferrer">
                        Public View
                      </a>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="aiEmpty">
                  No invoices found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="aiFoot">
        <div className="aiMuted">
          Showing <strong>{filtered.length}</strong> of {rows.length}
        </div>
        <div className="aiMuted">
          Next: build <code>/admin/invoices/edit/:blobId</code> (payments ledger + status automation).
        </div>
      </div>
    </div>
  );
}
